import React, { useEffect, useState, useCallback } from "react";
import { makeRequestAuth } from "../../_config/api";
import NoData from "../../utils/DataNotFound";
import { ToastContainer } from "react-toastify";
import { FiEdit2, FiTrash2, FiPlus, FiFileText, FiList } from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { useNavigate } from "react-router-dom"; // Navigation ke liye

import EditPracticeModal from "./EditPracticeModal";
import AddPracticeModal from "./AddPracticeModal";

export default function PracticeList() {
  const [list, setList] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [editData, setEditData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const navigate = useNavigate(); // Hook for navigation
  const pageSize = 10;

  // Keyword hata diya hai
  const fetchPracticeList = useCallback(async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/getPracticeList", {
        pageNo,
        size: pageSize,
      });
      setList(res?.data || []);
    } catch {
      console.log("data not found!");
    }
  }, [pageNo]);

  useEffect(() => {
    fetchPracticeList();
  }, [fetchPracticeList]);

  const handleDelete = async (practiceId) => {
    if (!window.confirm("Are you sure you want to delete this practice?"))
      return;
    try {
      await makeRequestAuth("post", "/admin/deletePractice", { practiceId });
      showSuccessToast("Practice deleted");
      fetchPracticeList();
    } catch {
      showErrorToast("Delete failed");
    }
  };

  // Function to navigate to Subject List page
  const handleViewSubjects = (item) => {
    // Navigate to new route with ID and Name in state
    navigate(`/admin/practice-subjects/${item._id}`, {
      state: { practiceName: item.name },
    });
  };

  return (
    <div className="p-4">
      <ToastContainer />

      {/* HEADER - Search Removed */}
      <div className="flex flex-row items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Practice Library</h2>
          <p className="text-gray-500 text-sm">Manage your practices</p>
        </div>

        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow-md transition-all active:scale-95"
        >
          <FiPlus size={18} />
          <span className="hidden sm:inline">Add Practice</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* TABLE */}
      {list.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
          <NoData title="No Practice Found!" />
        </div>
      ) : (
        <>
          {/* DESKTOP VIEW */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider font-semibold border-b">
                <tr>
                  <th className="px-6 py-4">#</th>
                  <th className="px-6 py-4">Icon</th>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4 text-center">Papers</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map((item, i) => (
                  <tr
                    key={item._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-gray-500">{i + 1}</td>
                    <td className="px-6 py-4">
                      <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center p-1 border">
                        <img
                          src={item.icon}
                          alt={item.name}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full text-xs font-bold">
                        <FiFileText /> {item.paper}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end items-center gap-2">
                        {/* VIEW SUBJECTS BUTTON (Redirects to new page) */}
                        <button
                          onClick={() => handleViewSubjects(item)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-md transition-colors mr-2"
                        >
                          <FiList size={14} />
                          Subjects
                        </button>

                        <button
                          onClick={() => setEditData(item)}
                          className="p-2 text-blue-600 bg-white border hover:bg-blue-50 rounded-lg"
                        >
                          <FiEdit2 size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="p-2 text-red-600 bg-white border hover:bg-red-50 rounded-lg"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE VIEW */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {list.map((item) => (
              <div
                key={item._id}
                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 bg-gray-50 rounded-lg p-2 border flex items-center justify-center">
                    <img
                      src={item.icon}
                      alt={item.name}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                    <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-0.5 rounded">
                      {item.paper} Papers
                    </span>
                  </div>
                </div>

                <div className="pt-3 border-t flex items-center gap-2">
                  {/* VIEW SUBJECTS BUTTON MOBILE */}
                  <button
                    onClick={() => handleViewSubjects(item)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100"
                  >
                    <FiList /> Subjects
                  </button>

                  <button
                    onClick={() => setEditData(item)}
                    className="p-2 text-blue-600 bg-gray-50 rounded-lg border"
                  >
                    <FiEdit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="p-2 text-red-600 bg-gray-50 rounded-lg border"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showAdd && (
        <AddPracticeModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => {
            setShowAdd(false);
            fetchPracticeList();
          }}
        />
      )}
      {editData && (
        <EditPracticeModal
          data={editData}
          onClose={() => setEditData(null)}
          onSuccess={() => {
            setEditData(null);
            fetchPracticeList();
          }}
        />
      )}
    </div>
  );
}
